import { CLIENT_APP_URL } from "@/data/constants";
import * as Sentry from "@sentry/react";
import { getCurrentUser, getCurrentUserToken } from "@/lib/auth";
import useFirebaseHandlers from "@/services/firebase/useFirebaseHandler";
import { TBillingInterval } from "@/types/app";
import { isYearlyBilledMonthly } from "@/utils/billingInterval";
import { withTimeout } from "@/utils/withTimeout";
import toast from "react-hot-toast";
import { CHECKOUT_TYPE } from "../../constants.checkout";
import { takeAbmConsent } from "../../utils/pendingAbmConsent";
import { buildAddPayload } from "../../utils/payloadUtils";
import { useCheckoutMutation } from "./useCheckoutMutation";

const ABM_CONSENT_WRITE_TIMEOUT_MS = 5000;

export interface TPurchaseSelection {
  productIds: string[];
  interval: TBillingInterval;
  couponCode?: string | null;
  discounts?: string[];
}

interface UseExecutePurchaseOptions {
  billingPeriod: TBillingInterval;
  onError?: (error: unknown) => void;
  countryCode?: string;
}

/**
 * Charges a plan selection outright, skipping the `/checkout` review step.
 *
 * A buyer with no saved card comes back as the 302 that
 * {@link useCheckoutMutation} follows to Stripe. The mutation is returned too,
 * for callers that need its pending state or a payload of their own.
 */
export function useExecutePurchase({
  billingPeriod,
  onError,
  countryCode,
}: UseExecutePurchaseOptions) {
  const mutation = useCheckoutMutation({
    checkoutType: CHECKOUT_TYPE.PURCHASE,
    billingPeriod,
    successRedirectUrl: `${CLIENT_APP_URL}/payment-successful`,
    failureRedirectUrl: `${CLIENT_APP_URL}/pricing`,
    countryCode,
  });
  const { updateAbmConsent } = useFirebaseHandlers();

  // Lands on users/{uid} next to the terms consent. Awaited so a Stripe
  // redirect can't cut the write off; a failure never blocks the purchase.
  async function recordAbmConsent(interval: TBillingInterval) {
    const consent = takeAbmConsent();
    if (!consent || !isYearlyBilledMonthly(interval)) return;

    const user = await getCurrentUser();
    if (!user) return;

    await withTimeout(
      updateAbmConsent(user.uid, consent),
      ABM_CONSENT_WRITE_TIMEOUT_MS,
      "recordAbmConsent.setDoc",
    ).catch((error) => Sentry.captureException(error));
  }

  async function executePurchase({
    productIds,
    interval,
    couponCode,
    discounts,
  }: TPurchaseSelection) {
    try {
      const token = await getCurrentUserToken();
      if (!token) throw new Error("Unable to get auth token");

      await recordAbmConsent(interval);

      mutation.mutate(
        {
          token,
          payload: {
            products: buildAddPayload(productIds.map((id) => ({ id }))),
            interval,
            discounts: [
              ...new Set([
                ...(couponCode ? [couponCode] : []),
                ...(discounts ?? []),
              ]),
            ],
          },
        },
        { onError: (error) => onError?.(error) },
      );
    } catch (error) {
      onError?.(error);

      toast.error("Couldn't start your purchase. Please try again.");
    }
  }

  return { executePurchase, mutation };
}
